import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import './Launch.css';
import PhoneInput from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import { isValidLaunchEmail, isValidLaunchPhone } from '../../utils/launchValidation';
import { useLegalDocument } from '../../hooks/useLegalDocument';
import { saveLaunchContact, updateLaunchLead } from '../../services/launchLead';
import LaunchChoiceStep from './LaunchChoiceStep';
import LaunchMilestone from './LaunchMilestone';
import {
    LAUNCH_STEPS,
    PROGRESS_STEPS,
    CAMPAIGN_GOAL_OPTIONS,
    PROMOTION_TYPE_OPTIONS,
    LAUNCH_LEAD_ID_KEY,
    getLaunchProgressIndex,
    getProgressStepNumber,
} from './launchSteps';

const DEFAULT_LAUNCH_AMOUNT = '59.00';
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const CREATIVE_TOKEN_KEY = 'launch_creative_token';

export default function Launch() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const screenId = searchParams.get('screenId');
    const launchPageSlug = searchParams.get('page');

    const [currentStep, setCurrentStep] = useState(LAUNCH_STEPS.WELCOME);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [message, setMessage] = useState('');
    const [uploadedImage, setUploadedImage] = useState(null);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [campaignName, setCampaignName] = useState('');

    const [launchLeadId, setLaunchLeadId] = useState(null);
    const [campaignGoals, setCampaignGoals] = useState('');
    const [campaignGoalsOther, setCampaignGoalsOther] = useState('');
    const [promotionType, setPromotionType] = useState('');
    const [promotionTypeOther, setPromotionTypeOther] = useState('');
    const [savingLead, setSavingLead] = useState(false);
    const [milestone, setMilestone] = useState(null);

    const [uploadedContent, setUploadedContent] = useState(null);
    const [launchSession, setLaunchSession] = useState(null);
    const [advertiserId, setAdvertiserId] = useState(null);
    const [isNewAdvertiser, setIsNewAdvertiser] = useState(false);
    const [temporaryPassword, setTemporaryPassword] = useState(null);
    const [screenCost, setScreenCost] = useState(DEFAULT_LAUNCH_AMOUNT);
    const [screenListingPhotoUrl, setScreenListingPhotoUrl] = useState(null);
    const [screenBrand, setScreenBrand] = useState('');
    const [screenLocationDescription, setScreenLocationDescription] = useState('');
    const [screenDisplayId, setScreenDisplayId] = useState(null);
    const [screenInfoLoading, setScreenInfoLoading] = useState(false);

    const [otpCode, setOtpCode] = useState('');
    const [phoneVerificationToken, setPhoneVerificationToken] = useState(null);
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [sendCooldownSec, setSendCooldownSec] = useState(0);
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [checkoutTosError, setCheckoutTosError] = useState('');
    const { doc: tosDoc } = useLegalDocument('advertiser_tos');
    const tosVersion = tosDoc?.version;
    const cooldownTimerRef = useRef(null);

    const showVerifySuccessSplash =
        currentStep === LAUNCH_STEPS.VERIFY && phoneVerified && Boolean(phoneVerificationToken);

    const progressIndex = getLaunchProgressIndex(currentStep);
    const progressStepNumber = getProgressStepNumber(currentStep);
    const showProgress = !showVerifySuccessSplash && currentStep !== LAUNCH_STEPS.SUCCESS && !milestone;

    const triggerMilestone = useCallback((msg, nextStep) => {
        setMilestone({ message: msg, nextStep });
    }, []);

    const clearMilestone = useCallback(() => {
        setMilestone((prev) => {
            if (prev?.nextStep != null) {
                setCurrentStep(prev.nextStep);
            }
            return null;
        });
    }, []);

    useEffect(() => {
        try {
            const stored = sessionStorage.getItem(LAUNCH_LEAD_ID_KEY);
            if (stored) {
                setLaunchLeadId(parseInt(stored, 10) || null);
            }
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        if (!screenId) {
            return undefined;
        }

        let cancelled = false;
        setScreenInfoLoading(true);

        const pageQuery = launchPageSlug
            ? `&page=${encodeURIComponent(launchPageSlug)}`
            : '';

        fetch(`${process.env.REACT_APP_API_URL}/launch/screen?screenId=${encodeURIComponent(screenId)}${pageQuery}`)
            .then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.success) {
                    throw new Error(data.error || 'Could not load screen details');
                }
                return data;
            })
            .then((data) => {
                if (cancelled) {
                    return;
                }
                if (data.screenCost) {
                    setScreenCost(String(data.screenCost));
                }
                if (data.listingPhotoUrl) {
                    setScreenListingPhotoUrl(data.listingPhotoUrl);
                }
                setScreenBrand(data.brand ? String(data.brand) : '');
                setScreenLocationDescription(
                    data.locationDescription ? String(data.locationDescription) : ''
                );
                setScreenDisplayId(data.screenId || null);
            })
            .catch(() => {
                if (!cancelled) {
                    navigate('/', { replace: true });
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setScreenInfoLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [screenId, launchPageSlug, navigate]);

    useEffect(() => () => {
        if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
        }
    }, []);

    const startSendCooldown = () => {
        setSendCooldownSec(60);
        if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = setInterval(() => {
            setSendCooldownSec((s) => {
                if (s <= 1) {
                    if (cooldownTimerRef.current) {
                        clearInterval(cooldownTimerRef.current);
                        cooldownTimerRef.current = null;
                    }
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
    };

    const handleSendVerificationCode = async () => {
        setError('');
        if (!isValidLaunchEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        if (!isValidLaunchPhone(phone)) {
            setError('Please enter a valid phone number');
            return;
        }
        setSendingCode(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/launch/phone/send-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Failed to send code');
            setCodeSent(true);
            setPhoneVerificationToken(null);
            setPhoneVerified(false);
            setOtpCode('');
            startSendCooldown();
        } catch (err) {
            setError(err.message || 'Failed to send code');
        } finally {
            setSendingCode(false);
        }
    };

    const handleVerifyOtp = async () => {
        setError('');
        if (!/^\d{6}$/.test((otpCode || '').trim())) {
            setError('Enter the 6-digit code from your text');
            return;
        }
        setVerifyingCode(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/launch/phone/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone, code: otpCode.trim() }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Verification failed');
            setPhoneVerificationToken(data.phoneVerificationToken);
            setPhoneVerified(true);
            setOtpCode('');
            setError('');
        } catch (err) {
            setError(err.message || 'Verification failed');
        } finally {
            setVerifyingCode(false);
        }
    };

    const handleContactNext = async () => {
        setError('');
        if (!isValidLaunchEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        if (!isValidLaunchPhone(phone)) {
            setError('Please enter a valid phone number');
            return;
        }

        setSavingLead(true);
        try {
            const data = await saveLaunchContact({ screenId, email, phone, page: launchPageSlug });
            const id = data.launchLeadId;
            setLaunchLeadId(id);
            try {
                sessionStorage.setItem(LAUNCH_LEAD_ID_KEY, String(id));
            } catch {
                // ignore
            }
            setPhoneVerified(false);
            setPhoneVerificationToken(null);
            setCodeSent(false);
            triggerMilestone("You're in!", LAUNCH_STEPS.CAMPAIGN_GOAL);
        } catch (err) {
            setError(err.message || 'Could not save your contact info');
        } finally {
            setSavingLead(false);
        }
    };

    const handleCampaignGoalSelect = async (value, otherText) => {
        setCampaignGoals(value);
        if (value === 'other' && otherText) {
            setCampaignGoalsOther(otherText);
        }
        setError('');
        if (launchLeadId) {
            try {
                await updateLaunchLead(launchLeadId, { campaignGoals: value });
            } catch (err) {
                console.warn('Could not save campaign goal:', err);
            }
        }
        triggerMilestone('Great choice!', LAUNCH_STEPS.PROMOTION_TYPE);
    };

    const handlePromotionTypeSelect = async (value, otherText) => {
        setPromotionType(value);
        if (value === 'other' && otherText) {
            setPromotionTypeOther(otherText);
        }
        setError('');
        if (launchLeadId) {
            try {
                await updateLaunchLead(launchLeadId, {
                    promotionType: value,
                    promotionTypeOther: value === 'other' ? otherText : undefined,
                });
            } catch (err) {
                console.warn('Could not save promotion type:', err);
            }
        }
        triggerMilestone('Almost there!', LAUNCH_STEPS.MESSAGE);
    };

    const handleMessageNext = () => {
        if (!message.trim()) {
            setError('Please enter a message for your campaign');
            return;
        }
        setCampaignName(message.substring(0, 50));
        setCurrentStep(LAUNCH_STEPS.UPLOAD);
        setError('');
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes((file.type || '').toLowerCase())) {
            setError('Unsupported format. Please use JPG, PNG, WEBP, or GIF');
            return;
        }

        if (file.size > 5242880) {
            setError('File size must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setUploadedImage(event.target.result);
            setError('');
        };
        reader.readAsDataURL(file);
    };

    const handleSaveUpload = async () => {
        if (!uploadedImage) {
            setError('Please upload an image');
            return;
        }
        setUploadedContent(uploadedImage);
        setCurrentStep(LAUNCH_STEPS.PREVIEW);
        setError('');
    };

    const handlePreviewNext = () => {
        triggerMilestone('Looking good!', LAUNCH_STEPS.VERIFY);
    };

    const handleVerificationNext = useCallback(async () => {
        setError('');
        if (!phoneVerified || !phoneVerificationToken) {
            setError('Please verify your phone number with the code we text you');
            return;
        }

        setIsLoading(true);
        setCurrentStep(LAUNCH_STEPS.PREPARING);
        setError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/launch/prepare`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    screenId,
                    email,
                    message,
                    phone,
                    phoneVerificationToken,
                    image: uploadedImage,
                    page: launchPageSlug || undefined,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to prepare checkout');
            }

            const data = await response.json();
            setLaunchSession(data.sessionId);
            setAdvertiserId(data.advertiserId);
            setIsNewAdvertiser(data.isNewAdvertiser);
            setTemporaryPassword(data.temporaryPassword);
            if (data.screenCost) {
                setScreenCost(String(data.screenCost));
            }

            if (!data.creativeToken) {
                throw new Error('Could not save your creative for checkout. Please try again.');
            }

            try {
                sessionStorage.removeItem('launch_image');
                sessionStorage.removeItem('launch_message');
                sessionStorage.setItem(CREATIVE_TOKEN_KEY, data.creativeToken);
            } catch (storageErr) {
                console.warn('Could not persist launch creative token:', storageErr);
                throw new Error('Could not save your creative for checkout. Please try again.');
            }

            setCurrentStep(LAUNCH_STEPS.CHECKOUT);
        } catch (err) {
            console.error('Prepare checkout error:', err);
            setError(err.message || 'Failed to prepare checkout');
            setPhoneVerified(false);
            setPhoneVerificationToken(null);
            setCurrentStep(LAUNCH_STEPS.VERIFY);
        } finally {
            setIsLoading(false);
        }
    }, [screenId, launchPageSlug, email, message, phone, phoneVerificationToken, phoneVerified, uploadedImage]);

    const handleVerificationNextRef = useRef(handleVerificationNext);
    handleVerificationNextRef.current = handleVerificationNext;

    useEffect(() => {
        if (currentStep !== LAUNCH_STEPS.VERIFY || !phoneVerified || !phoneVerificationToken) {
            return undefined;
        }
        const id = window.setTimeout(() => {
            handleVerificationNextRef.current();
        }, 1800);
        return () => window.clearTimeout(id);
    }, [currentStep, phoneVerified, phoneVerificationToken]);

    const handleStripeCheckout = async () => {
        if (!tosAccepted) {
            setCheckoutTosError('You must agree to the Terms of Service before proceeding to payment.');
            return;
        }
        setCheckoutTosError('');

        try {
            setIsLoading(true);

            if (!sessionStorage.getItem(CREATIVE_TOKEN_KEY)) {
                throw new Error('Your creative was not saved. Go back, verify your phone again, and retry checkout.');
            }

            try {
                sessionStorage.setItem(
                    'launch_checkout_meta',
                    JSON.stringify({
                        isNewAdvertiser: Boolean(isNewAdvertiser),
                        temporaryPassword: isNewAdvertiser ? temporaryPassword : null,
                        email,
                        phone,
                        campaignName,
                        screenId,
                        advertiserId,
                        launchLeadId,
                    })
                );
            } catch (e) {
                console.warn('Could not persist launch checkout meta:', e);
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/launch/checkout/create-stripe-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    screenId,
                    advertiserId,
                    email,
                    campaignName,
                    page: launchPageSlug || undefined,
                }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to create Stripe session');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else if (data.id) {
                window.location.href = `${process.env.REACT_APP_STRIPE_CHECKOUT_BASE_URL || ''}/${data.id}`;
            } else {
                throw new Error('Invalid Stripe session response');
            }
        } catch (err) {
            console.error('Stripe checkout error:', err);
            setError(err.message || 'Stripe checkout failed');
        } finally {
            setIsLoading(false);
        }
    };

    if (error && currentStep === LAUNCH_STEPS.CHECKOUT) {
        return (
            <div className="launch-container">
                <div className="launch-error">
                    <h2>Something went wrong</h2>
                    <p>{error}</p>
                    <button type="button" onClick={() => window.location.reload()}>Try Again</button>
                </div>
            </div>
        );
    }

    if (!screenId) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="launch-container">
            {!showVerifySuccessSplash && !milestone && (
                <div className="launch-header">
                    <div className="launch-logo">INVERT</div>
                </div>
            )}

            {showProgress && (
                <div className="launch-steps-indicator">
                    <p className="launch-progress-label">
                        Step {progressStepNumber} of {PROGRESS_STEPS.length}
                    </p>
                    <div className="steps-progress">
                        {PROGRESS_STEPS.map((_, idx) => (
                            <div
                                key={idx}
                                className={`step ${idx === progressIndex ? 'active' : ''} ${idx < progressIndex ? 'completed' : ''}`}
                            />
                        ))}
                    </div>
                </div>
            )}

            <div className="launch-content">
                {milestone && (
                    <LaunchMilestone message={milestone.message} onDone={clearMilestone} />
                )}

                {!milestone && currentStep === LAUNCH_STEPS.WELCOME && (
                    <div className="launch-step">
                        <h1>Launch on an intelligent screen</h1>
                        <p className="step-subtitle">Reach customers on an AI-powered business display</p>
                        {(screenBrand || screenLocationDescription || screenDisplayId) && (
                            <div className="launch-screen-meta">
                                {screenBrand && (
                                    <p className="launch-screen-brand">{screenBrand}</p>
                                )}
                                {screenLocationDescription && (
                                    <p
                                        className="launch-screen-description"
                                        title={screenLocationDescription}
                                    >
                                        {screenLocationDescription}
                                    </p>
                                )}
                                {!screenBrand && !screenLocationDescription && screenDisplayId && (
                                    <p className="launch-screen-brand">Screen #{screenDisplayId}</p>
                                )}
                            </div>
                        )}
                        <div className="launch-screen-visual">
                            {screenListingPhotoUrl ? (
                                <img
                                    className="launch-screen-photo"
                                    src={screenListingPhotoUrl}
                                    alt={screenBrand || screenLocationDescription || 'Screen you scanned'}
                                />
                            ) : (
                                <div className="launch-icon-container">
                                    <div className="launch-arrow-circle">→</div>
                                </div>
                            )}
                        </div>
                        <p className="launch-duration-note">
                            After approval, your creative will run on this screen for one month.
                        </p>
                        <p className="launch-pricing-note">
                            {screenInfoLoading
                                ? 'Loading pricing…'
                                : `One-month placement · $${screenCost}`}
                        </p>
                        <button
                            type="button"
                            className="launch-btn primary"
                            onClick={() => { setCurrentStep(LAUNCH_STEPS.CONTACT); setError(''); }}
                        >
                            Let&apos;s Go
                        </button>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.CONTACT && (
                    <div className="launch-step">
                        <h1>Let&apos;s stay in touch</h1>
                        <p className="step-subtitle">We&apos;ll save this now so you can pick up where you left off</p>
                        <input
                            type="email"
                            className="launch-input"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setPhoneVerified(false);
                                setPhoneVerificationToken(null);
                                setCodeSent(false);
                            }}
                        />
                        <label className="launch-phone-label" htmlFor="launch-phone">Mobile number</label>
                        <PhoneInput
                            id="launch-phone"
                            className="launch-phone-input"
                            country="US"
                            defaultCountry="US"
                            value={phone}
                            onChange={(value) => {
                                setPhone(value || '');
                                setPhoneVerified(false);
                                setPhoneVerificationToken(null);
                                setCodeSent(false);
                            }}
                            placeholder="(555) 000-0000"
                        />
                        {error && <p className="launch-error-text">{error}</p>}
                        <button
                            type="button"
                            className="launch-btn primary"
                            onClick={handleContactNext}
                            disabled={savingLead}
                        >
                            {savingLead ? 'Saving…' : 'Continue →'}
                        </button>
                        <button
                            type="button"
                            className="launch-btn secondary launch-choice-back"
                            onClick={() => { setCurrentStep(LAUNCH_STEPS.WELCOME); setError(''); }}
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.CAMPAIGN_GOAL && (
                    <LaunchChoiceStep
                        title="What's your main goal?"
                        subtitle="Pick the one that fits best — we'll tailor your experience"
                        options={CAMPAIGN_GOAL_OPTIONS}
                        selectedValue={campaignGoals}
                        otherText={campaignGoalsOther}
                        onOtherTextChange={setCampaignGoalsOther}
                        otherPlaceholder="Describe your goal…"
                        onSelect={handleCampaignGoalSelect}
                        onBack={() => setCurrentStep(LAUNCH_STEPS.CONTACT)}
                    />
                )}

                {!milestone && currentStep === LAUNCH_STEPS.PROMOTION_TYPE && (
                    <LaunchChoiceStep
                        title="What are you promoting?"
                        subtitle="This helps us understand your campaign"
                        options={PROMOTION_TYPE_OPTIONS}
                        selectedValue={promotionType}
                        otherText={promotionTypeOther}
                        onOtherTextChange={setPromotionTypeOther}
                        otherPlaceholder="Describe your promotion…"
                        onSelect={handlePromotionTypeSelect}
                        onBack={() => setCurrentStep(LAUNCH_STEPS.CAMPAIGN_GOAL)}
                    />
                )}

                {!milestone && currentStep === LAUNCH_STEPS.MESSAGE && (
                    <div className="launch-step">
                        <h1>What&apos;s your message?</h1>
                        <p className="step-subtitle">This becomes your campaign name on screen</p>
                        <input
                            type="text"
                            className="launch-input"
                            placeholder="Ice cream shop sale!"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={100}
                        />
                        {error && <p className="launch-error-text">{error}</p>}
                        <button type="button" className="launch-btn primary" onClick={handleMessageNext}>
                            Next →
                        </button>
                        <button
                            type="button"
                            className="launch-btn secondary launch-choice-back"
                            onClick={() => { setCurrentStep(LAUNCH_STEPS.PROMOTION_TYPE); setError(''); }}
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.UPLOAD && (
                    <div className="launch-step">
                        <h1>Upload your creative</h1>
                        <p className="step-subtitle">JPG, PNG, WEBP, or GIF — up to 5 MB</p>
                        <label className="launch-upload-area">
                            {uploadedImage ? (
                                <div className="upload-preview">
                                    <img src={uploadedImage} alt="Uploaded" />
                                    <p>Click to change image</p>
                                </div>
                            ) : (
                                <div className="upload-placeholder">
                                    <div className="upload-icon">↑</div>
                                    <p>Upload an Image!</p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                            />
                        </label>
                        {error && <p className="launch-error-text">{error}</p>}
                        <button
                            type="button"
                            className="launch-btn primary"
                            onClick={handleSaveUpload}
                            disabled={!uploadedImage || isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Save & Continue →'}
                        </button>
                        <button
                            type="button"
                            className="launch-btn secondary launch-choice-back"
                            onClick={() => { setCurrentStep(LAUNCH_STEPS.MESSAGE); setError(''); }}
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.PREPARING && (
                    <div className="launch-step">
                        <h1>Almost there!</h1>
                        <h2>Preparing checkout...</h2>
                        <div className="launch-spinner">
                            <div className="spinner-circle">↑</div>
                        </div>
                        <p>Setting up your campaign...</p>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.PREVIEW && uploadedContent && (
                    <div className="launch-step">
                        <h1>Does this look right?</h1>
                        <p className="step-subtitle">This is what will appear on the screen</p>
                        <div className="launch-preview-container">
                            <div className="preview-card portrait">
                                <img src={uploadedContent} alt="Uploaded content" />
                                <p>Uploaded Content</p>
                            </div>
                        </div>
                        <div className="preview-buttons">
                            <button type="button" className="launch-btn primary" onClick={handlePreviewNext}>
                                Yes! →
                            </button>
                            <button
                                type="button"
                                className="launch-btn secondary"
                                onClick={() => { setCurrentStep(LAUNCH_STEPS.UPLOAD); setUploadedContent(null); }}
                            >
                                ← Try again
                            </button>
                        </div>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.VERIFY && (
                    <div className="launch-step">
                        {showVerifySuccessSplash ? (
                            <div className="launch-verify-success-splash" aria-live="polite">
                                <div className="launch-confetti" aria-hidden="true">
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <span
                                            key={i}
                                            className="launch-confetti-piece"
                                            style={{
                                                left: `${5 + (i * 4) % 90}%`,
                                                animationDelay: `${(i % 8) * 0.05}s`,
                                            }}
                                        />
                                    ))}
                                </div>
                                <div className="launch-verify-success-icon" aria-hidden="true">✓</div>
                                <p className="launch-verify-success-title">Phone verified</p>
                                <p className="launch-verify-success-sub">Taking you to checkout…</p>
                            </div>
                        ) : (
                            <>
                                <h1>Verify your phone</h1>
                                <p className="step-subtitle">We&apos;ll text a 6-digit code to {phone}</p>
                                <div className="launch-otp-block launch-otp-verify-layout">
                                    <label className="launch-phone-label" htmlFor="launch-otp">6-digit code</label>
                                    <input
                                        id="launch-otp"
                                        className="launch-input"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        placeholder="------"
                                        value={otpCode}
                                        disabled={!codeSent}
                                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    />
                                    {!codeSent && (
                                        <button
                                            type="button"
                                            className="launch-btn checkout"
                                            onClick={handleSendVerificationCode}
                                            disabled={sendingCode}
                                        >
                                            {sendingCode ? 'Sending…' : 'Text me a verification code'}
                                        </button>
                                    )}
                                    {codeSent && otpCode.length < 6 && (
                                        <p className="launch-verify-code-hint">Enter the code we sent to your phone.</p>
                                    )}
                                    {codeSent && otpCode.length === 6 && (
                                        <button
                                            type="button"
                                            className="launch-btn checkout"
                                            onClick={handleVerifyOtp}
                                            disabled={verifyingCode}
                                        >
                                            {verifyingCode ? 'Checking…' : 'Verify code'}
                                        </button>
                                    )}
                                    {codeSent && (
                                        <button
                                            type="button"
                                            className="launch-verify-resend"
                                            onClick={handleSendVerificationCode}
                                            disabled={sendingCode || sendCooldownSec > 0}
                                        >
                                            {sendCooldownSec > 0
                                                ? `Resend code (${sendCooldownSec}s)`
                                                : sendingCode
                                                    ? 'Sending…'
                                                    : 'Resend code'}
                                        </button>
                                    )}
                                </div>
                                {error && <p className="launch-error-text">{error}</p>}
                                <button
                                    type="button"
                                    className="launch-btn secondary launch-verify-back"
                                    onClick={() => {
                                        setCurrentStep(LAUNCH_STEPS.PREVIEW);
                                        setError('');
                                    }}
                                >
                                    ← Back
                                </button>
                            </>
                        )}
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.CHECKOUT && (
                    <div className="launch-step">
                        <h1>Last step</h1>
                        <p className="step-subtitle">Complete payment to go live on this screen</p>
                        <div className="launch-checkout-summary">
                            <p>Amount: ${screenCost}</p>
                            <p className="launch-checkout-duration">
                                Includes one month of display on this screen after approval.
                            </p>
                        </div>
                        <label className="launch-checkbox launch-checkout-tos">
                            <input
                                type="checkbox"
                                checked={tosAccepted}
                                onChange={(e) => {
                                    setTosAccepted(e.target.checked);
                                    if (e.target.checked) {
                                        setCheckoutTosError('');
                                    }
                                }}
                            />
                            <span>
                                I agree to the{' '}
                                <Link
                                    to="/tos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="launch-tos-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    INVERT Terms of Service
                                </Link>{' '}
                                {tosVersion ? `(version ${tosVersion})` : ''}, including launch and subscription advertising.
                            </span>
                        </label>
                        {checkoutTosError && (
                            <p className="launch-error-text launch-checkout-tos-error">{checkoutTosError}</p>
                        )}
                        <div className="checkout-methods">
                            <div className="stripe-cta">
                                <button
                                    type="button"
                                    className="launch-btn checkout"
                                    onClick={handleStripeCheckout}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Processing…' : 'Checkout'}
                                </button>
                                <p className="stripe-cta-subtext">Secure card payment powered by Stripe.</p>
                            </div>
                        </div>
                    </div>
                )}

                {!milestone && currentStep === LAUNCH_STEPS.SUCCESS && (
                    <div className="launch-step launch-success-card">
                        <div className="launch-success-icon" aria-hidden="true">✓</div>
                        <h1>You are all set</h1>
                        <h2>Your campaign is in review</h2>
                        <p className="launch-success-lead">
                            We have your creative and our team will review it shortly. Watch your inbox for your receipt and confirmation—we also notified our team.
                        </p>
                        {isNewAdvertiser && temporaryPassword && (
                            <div className="success-info">
                                <p>Your account has been created. Check your email for your temporary password and change it after you log in.</p>
                            </div>
                        )}
                        <button
                            type="button"
                            className="launch-btn primary"
                            onClick={() => { window.location.href = '/'; }}
                        >
                            Back to Home
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
